<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" dir="rtl">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div class="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
          <UserPlus class="h-6 w-6 text-blue-600" />
        </div>
        <h2 class="mt-6 text-3xl font-bold text-gray-900">Create Account</h2>
        <p class="mt-2 text-sm text-gray-600">
          Join our legal consultation platform
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
        <div class="flex">
          <X class="h-5 w-5 text-red-400 ml-2" />
          <div class="text-sm text-red-800">{{ error }}</div>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border border-green-200 rounded-md p-4">
        <div class="flex">
          <Check class="h-5 w-5 text-green-400 ml-2" />
          <div class="text-sm text-green-800">{{ successMessage }}</div>
        </div>
      </div>

      <!-- Registration Form -->
      <form class="mt-8 space-y-6" @submit.prevent="handleRegister">
        <div class="space-y-4">
          <!-- User Type Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 text-right">
              Account Type
            </label>
            <div class="mt-1">
              <select
                v-model="form.userType"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                :disabled="isLoading"
              >
                <option value="">Select account type</option>
                <option value="client">Client - Looking for legal services</option>
                <option value="user">User - General access</option>
                <option value="lawyer">Lawyer - Legal professional</option>
                <option value="firm_admin">Firm Admin - Manage law firm</option>
              </select>
            </div>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 text-right">
              Email Address
            </label>
            <div class="mt-1">
              <input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Enter your email"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Full Name -->
          <div>
            <label for="fullName" class="block text-sm font-medium text-gray-700 text-right">
              Full Name
            </label>
            <div class="mt-1">
              <input
                id="fullName"
                v-model="form.fullName"
                name="fullName"
                type="text"
                autocomplete="name"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Enter your full name"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Full Name (Arabic) -->
          <div>
            <label for="fullNameAr" class="block text-sm font-medium text-gray-700 text-right">
              Full Name (Arabic)
            </label>
            <div class="mt-1">
              <input
                id="fullNameAr"
                v-model="form.fullNameAr"
                name="fullNameAr"
                type="text"
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Enter your name in Arabic"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700 text-right">
              Phone Number
            </label>
            <div class="mt-1">
              <input
                id="phone"
                v-model="form.phone"
                name="phone"
                type="tel"
                autocomplete="tel"
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Enter your phone number"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Specialization (for lawyers) -->
          <div v-if="form.userType === 'lawyer' || form.userType === 'firm_admin'">
            <label for="specialization" class="block text-sm font-medium text-gray-700 text-right">
              Specialization
            </label>
            <div class="mt-1">
              <input
                id="specialization"
                v-model="form.specialization"
                name="specialization"
                type="text"
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="e.g., Corporate Law, Family Law"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 text-right">
              Password
            </label>
            <div class="mt-1">
              <input
                id="password"
                v-model="form.password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Create a password"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 text-right">
              Confirm Password
            </label>
            <div class="mt-1">
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                name="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                autocomplete="new-password"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Confirm your password"
                :disabled="isLoading"
              />
            </div>
          </div>
        </div>

        <!-- Terms and Privacy -->
        <div class="flex items-center">
          <input
            id="agree-terms"
            v-model="agreeTerms"
            name="agree-terms"
            type="checkbox"
            required
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label for="agree-terms" class="mr-2 block text-sm text-gray-900">
            I agree to the 
            <a href="#" class="text-blue-600 hover:text-blue-500">Terms of Service</a> and 
            <a href="#" class="text-blue-600 hover:text-blue-500">Privacy Policy</a>
          </label>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="isLoading || !agreeTerms"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading" class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
              Creating account...
            </span>
            <span v-else>Create Account</span>
          </button>
        </div>

        <!-- Login Link -->
        <div class="text-center">
          <span class="text-sm text-gray-600">
            Already have an account? 
          </span>
          <router-link
            to="/login"
            class="font-medium text-blue-600 hover:text-blue-500 text-sm"
          >
            Sign in
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { UserPlus, X, Check } from 'lucide-vue-next';
import type { RegisterData } from '../types/auth';

const router = useRouter();
const authStore = useAuthStore();

// Form state
const form = reactive<RegisterData>({
  email: '',
  password: '',
  fullName: '',
  fullNameAr: '',
  phone: '',
  userType: 'client',
  specialization: '',
});

const confirmPassword = ref('');
const agreeTerms = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const error = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Computed
const isLoading = computed(() => authStore.isLoading);

// Methods
const handleRegister = async () => {
  // Validation
  if (!form.email || !form.password || !form.fullName || !form.userType) {
    error.value = 'Please fill in all required fields';
    return;
  }

  if (form.password !== confirmPassword.value) {
    error.value = 'Passwords do not match';
    return;
  }

  if (form.password.length < 6) {
    error.value = 'Password must be at least 6 characters long';
    return;
  }

  if (!agreeTerms.value) {
    error.value = 'Please agree to the terms and conditions';
    return;
  }

  error.value = null;
  successMessage.value = null;

  try {
    const response = await authStore.register(form);
    
    if (response.success) {
      successMessage.value = 'Account created successfully! Redirecting...';
      
      // Redirect based on user type
      setTimeout(() => {
        const userType = authStore.user?.userType;
        if (userType === 'lawyer') {
          router.push('/lawyers');
        } else if (userType === 'firm_admin') {
          router.push('/firm-offices');
        } else {
          router.push('/');
        }
      }, 1500);
    } else {
      error.value = response.error || 'Registration failed';
    }
  } catch (err: any) {
    error.value = err.message || 'An unexpected error occurred';
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Hide app navigation on register page */
:deep(#app > nav) {
  display: none;
}
</style>
